import { makeAutoObservable, runInAction } from 'mobx';
import partnerInventoryService from '../services/partnerInventoryService';

class PartnerInventoryStore {
  inventory = [];
  currentInventory = null;
  loading = false;
  error = null;

  // Filters
  searchQuery = '';
  stockFilter = null; // 'IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK'
  activeFilter = null; // true, false, null

  // Add inventory workflow state
  matchedProduct = null;
  selectedVariant = null;
  productVariants = [];

  constructor() {
    makeAutoObservable(this);
  }

  // Computed values
  get filteredInventory() {
    let filtered = this.inventory;

    // Filter by search query (product name or SKU)
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.product_name?.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query)
      );
    }

    // Filter by stock status
    if (this.stockFilter === 'IN_STOCK') {
      filtered = filtered.filter(item => item.stock_quantity > 10);
    } else if (this.stockFilter === 'LOW_STOCK') {
      filtered = filtered.filter(item => item.stock_quantity > 0 && item.stock_quantity <= 10);
    } else if (this.stockFilter === 'OUT_OF_STOCK') {
      filtered = filtered.filter(item => item.stock_quantity === 0);
    }

    // Filter by active status
    if (this.activeFilter !== null) {
      filtered = filtered.filter(item => item.is_active === this.activeFilter);
    }

    return filtered;
  }

  // Actions
  setSearchQuery(query) {
    this.searchQuery = query;
  }

  setStockFilter(filter) {
    this.stockFilter = filter;
  }

  setActiveFilter(filter) {
    this.activeFilter = filter;
  }

  async fetchInventory() {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.getInventory();

      runInAction(() => {
        if (result.success) {
          this.inventory = result.data;
        } else {
          this.error = result.error || 'Failed to fetch inventory';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch inventory';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async fetchInventoryById(inventoryId) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.getInventoryById(inventoryId);

      runInAction(() => {
        if (result.success) {
          this.currentInventory = result.data;
        } else {
          this.error = result.error || 'Failed to fetch inventory item';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch inventory item';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async createInventory(data) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.createInventory(data);

      runInAction(() => {
        if (result.success) {
          this.inventory.push(result.data);
        } else {
          this.error = result.error || 'Failed to create inventory';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to create inventory';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async updateInventory(inventoryId, data) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.updateInventory(inventoryId, data);

      runInAction(() => {
        if (result.success) {
          // Update in list
          const index = this.inventory.findIndex(item => item.id === inventoryId);
          if (index !== -1) {
            this.inventory[index] = result.data;
          }
          // Update current if it's the same one
          if (this.currentInventory && this.currentInventory.id === inventoryId) {
            this.currentInventory = result.data;
          }
        } else {
          this.error = result.error || 'Failed to update inventory';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to update inventory';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async deleteInventory(inventoryId) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.deleteInventory(inventoryId);

      runInAction(() => {
        if (result.success) {
          // Remove from list or mark as inactive
          const index = this.inventory.findIndex(item => item.id === inventoryId);
          if (index !== -1) {
            this.inventory[index].is_active = false;
          }
        } else {
          this.error = result.error || 'Failed to delete inventory';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete inventory';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Workflow methods for adding inventory
  async matchProduct(productName) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.matchProduct(productName);

      runInAction(() => {
        if (result.success) {
          this.matchedProduct = result.data.product;
        } else {
          this.error = result.error || 'Failed to match product';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to match product';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async fetchProductVariants(productId) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.getProductVariants(productId);

      runInAction(() => {
        if (result.success) {
          this.productVariants = result.data;
        } else {
          this.error = result.error || 'Failed to fetch variants';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch variants';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async createVariant(productId, variantData) {
    this.loading = true;
    this.error = null;

    try {
      const result = await partnerInventoryService.createVariant(productId, variantData);

      runInAction(() => {
        if (result.success) {
          this.selectedVariant = result.data;
          this.productVariants.push(result.data);
        } else {
          this.error = result.error || 'Failed to create variant';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to create variant';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  setSelectedVariant(variant) {
    this.selectedVariant = variant;
  }

  // Reset workflow state
  resetWorkflow() {
    this.matchedProduct = null;
    this.selectedVariant = null;
    this.productVariants = [];
    this.error = null;
  }

  // Reset store
  reset() {
    this.inventory = [];
    this.currentInventory = null;
    this.loading = false;
    this.error = null;
    this.searchQuery = '';
    this.stockFilter = null;
    this.activeFilter = null;
    this.resetWorkflow();
  }
}

export default PartnerInventoryStore;
